/* eslint-disable no-param-reassign */
import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  EMSPPreauthAPIResponse,
  EMSPPreauthInitialProperties,
  EMSPPreauthInterface,
  RequestEMSPPreauthInterface,
} from '../../interface/emsp-preauth.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import emspPreauthApi from '../../utils/api/v2/emsp-preauth';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Thunk to fetch all EMSP preauth records.
 * @param {RequestEMSPPreauthInterface} data - Query payload for fetching preauth records.
 * @returns {Promise<EMSPPreauthAPIResponse>} Promise resolving to preauth API response.
 */
export const fetchAllPreauths = createAsyncThunk<
  EMSPPreauthAPIResponse,
  RequestEMSPPreauthInterface
>(
  'emspPreauth/fetchAllPreauths',
  async (data: RequestEMSPPreauthInterface, { dispatch }) => {
    try {
      const response = await emspPreauthApi.fetchPreauthList(removeEmptyKeys(data));
      const rawResults = response?.data?.results || [];
      const results = rawResults.map((item: EMSPPreauthInterface) => ({
        ...item,
        id: item._id,
        date: ConvertUTCtoDate(item.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(item.createdAt)),
      }));

      return {
        results,
        page: response?.data?.page || 1,
        limit: response?.data?.limit || 20,
        totalPages: response?.data?.totalPages || 0,
        totalResults: response?.data?.totalResults || results.length,
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch preauth records',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to refresh EMSP preauth records without triggering main loader.
 * @param {RequestEMSPPreauthInterface} data - Query payload for refreshing preauth records.
 * @returns {Promise<EMSPPreauthAPIResponse>} Promise resolving to preauth API response.
 */
export const refreshAllPreauths = createAsyncThunk<
  EMSPPreauthAPIResponse,
  RequestEMSPPreauthInterface
>(
  'emspPreauth/refreshAllPreauths',
  async (data: RequestEMSPPreauthInterface, { dispatch }) => {
    try {
      const response = await emspPreauthApi.fetchPreauthList(removeEmptyKeys(data));
      const rawResults = response?.data?.results || [];
      const results = rawResults.map((item: EMSPPreauthInterface) => ({
        ...item,
        id: item._id,
        date: ConvertUTCtoDate(item.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(item.createdAt)),
      }));

      return {
        results,
        page: response?.data?.page || 1,
        limit: response?.data?.limit || 20,
        totalPages: response?.data?.totalPages || 0,
        totalResults: response?.data?.totalResults || results.length,
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to refresh preauth records',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to fetch filter options for EMSP preauth list.
 * @returns {Promise<CommonFilterProperties[]>} Promise resolving to common filter properties.
 */
export const fetchPreauthFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'emspPreauth/fetchPreauthFilters',
  async (_, { dispatch }) => {
    try {
      const response = await emspPreauthApi.fetchPreauthListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch filters',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      return [];
    }
  },
);

/**
 * Default available filter definitions for EMSP preauth.
 */
const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Status',
    key: 'status',
    type: filterType.SELECT_FIELD,
    value: [],
    listOfValue: [
      { label: 'Reserved', value: 'RESERVED' },
      { label: 'Linked', value: 'LINKED' },
      { label: 'Captured', value: 'CAPTURED' },
      { label: 'Released', value: 'RELEASED' },
      { label: 'Orphaned', value: 'ORPHANED' },
      { label: 'Pending', value: 'PENDING' },
      { label: 'Failed', value: 'FAILED' },
      { label: 'Expired', value: 'EXPIRED' },
    ],
    applyFilter: false,
    enable: true,
  },
  {
    name: 'Payment Method',
    key: 'paymentMethod',
    type: filterType.SELECT_FIELD,
    value: [],
    listOfValue: [
      { label: 'Card', value: 'card' },
      { label: 'Wallet', value: 'wallet' },
      { label: 'Apple Pay', value: 'applepay' },
    ],
    applyFilter: false,
    enable: true,
  },
  {
    name: 'Date Range',
    key: 'startDate_endDate',
    key1: 'startDate',
    value1: '',
    key2: 'endDate',
    value2: '',
    value: '',
    type: filterType.DATE_RANGE,
    applyFilter: false,
    enable: true,
  },
];

/**
 * Initial state for EMSP Preauth slice.
 */
const initialState: EMSPPreauthInitialProperties = {
  preauths: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

/**
 * Redux slice for EMSP Preauth management.
 */
export const emspPreauthSlice = createSlice({
  name: 'emspPreauth',
  initialState,
  reducers: {
    /**
     * Update EMSP Preauth filters.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     * @param {PayloadAction<FilterUpdateProperties>} action - Payload containing filter update payload.
     */
    updatePreauthFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Reset filter applied status flag.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     */
    disablePreauthFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set table sort criteria.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     * @param {PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>} action - Sort metadata.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * Clear all EMSP preauth state data.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     */
    clearPreauthData: (state) => {
      Object.assign(state, initialState);
    },
  },
  /**
   * Extra reducers for async thunk actions.
   * @param {ActionReducerMapBuilder<EMSPPreauthInitialProperties>} builder - Redux builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<EMSPPreauthInitialProperties>) => {
    builder
      .addCase(fetchAllPreauths.pending, (state: EMSPPreauthInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAllPreauths.fulfilled,
        (
          state: EMSPPreauthInitialProperties,
          action: PayloadAction<EMSPPreauthAPIResponse>,
        ) => {
          state.loading = false;
          state.preauths = action.payload.results;
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAllPreauths.rejected, (state: EMSPPreauthInitialProperties) => {
        state.loading = false;
      });

    builder.addCase(
      refreshAllPreauths.fulfilled,
      (
        state: EMSPPreauthInitialProperties,
        action: PayloadAction<EMSPPreauthAPIResponse>,
      ) => {
        state.preauths = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );

    builder
      .addCase(fetchPreauthFilters.pending, (state: EMSPPreauthInitialProperties) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchPreauthFilters.fulfilled,
        (
          state: EMSPPreauthInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          if (action.payload && action.payload.length > 0) {
            state.filters = [...state.filters, ...action.payload].filter(
              (object, index, self) => index === self.findIndex((t) => t.key === object.key),
            );
          }
        },
      )
      .addCase(fetchPreauthFilters.rejected, (state: EMSPPreauthInitialProperties) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  updatePreauthFilters,
  disablePreauthFilterApplied,
  setSortData,
  clearPreauthData,
} = emspPreauthSlice.actions;

export default emspPreauthSlice.reducer;
